import {cx} from "emotion";

import {BlockGuestAccess}    from "@Common/logic/block-guest-access";
import {cs}                  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {UseState}            from "@Common/react/use-state";
import {cascade, cascadeSum} from "@Common/utils/cascade";
import {merge}               from "@Common/utils/collections";
import {chain}               from "@Common/utils/fs";
import {isSuperSmall}        from "@Common/utils/math/is-super-small";

import {CorporateBillingAccountsBase} from "../../../../common/heavy/corporate-billing-accounts/corporate-billing-accounts-base";
import {PromptApprovalConflict}       from "../common/prompt-approval-conflict/prompt-approval-conflict";
import {BillingAccountStoreActions}   from "../store/actions/payments/billing-account-store-actions";
import {reloadCart}                   from "../store/actions/reload-cart";

import {PaymentPanel} from "./common/payment-panel";

export const CorporateBillingAccounts = ({store, summary, next: rootNext}) => cs(
    consumeContext("apis"),
    ["billingAccounts", (_, next) => next(chain(
        cascade(store.cart.items, "[*].billingAvailable.billingAccount[*]", (billingAccount, {}, {0: item}) => ({
            billingAccount,
            amount: toAmount(
                billingAccount.amountAvailable,
                cascadeSum(item.itemTotals, "[*].amount", (amount,{},{0:p}) => (p.type !== "promoCode" ? 1 : -1) * amount),
                item.selections.unitPrice,
            ),
        })),
        (_) => merge(_, ({billingAccount}) => billingAccount.id, (acc1, acc2) => ({
            billingAccount: acc1.billingAccount,
            amount: acc1.amount + acc2.amount,
        })),
        (_) => _.map((acc) => ({
            ...acc,
            amount: Math.min(
                acc.amount,
                summary.balance,
            ),
        })),
    ))],

    ["blockGuestAccess", (_, next) => BlockGuestAccess({next})],

    ({billingAccounts, blockGuestAccess}, next) => !billingAccounts.length || blockGuestAccess ? rootNext({
        invalid: false,
        render: () => null,
    }) : next(),

    ["actions", ({apis}, next) => next(BillingAccountStoreActions({clearCards: isSuperSmall(summary.remain), apis}))],

    ["promptApprovalConflict", (_, next) => PromptApprovalConflict({
        cart: store.cart,
        next,
    })],
    ["addBilling", ({actions, promptApprovalConflict, apis,}, next) => next(async ({billingId, fields}) => {
        const resultCart = await store.dispatch(
            actions.addBillingAccount({
                billingId,
                fields,
            })
        );
        const cartConflict = (
            resultCart.items.some((item) => item.approvalInfo?.approvalRequired) &&
            resultCart.items.some((item) => (
                !item.approvalInfo?.approvalRequired ||
                item.approvalInfo?.idOrderApproval !== resultCart.items[0].approvalInfo?.idOrderApproval
            ))
        );
        if (cartConflict) {
            const count = {};
            for (const idOrderApproval of resultCart.items.map((item) => item.approvalInfo?.idOrderApproval)) {
                count[idOrderApproval] = (count[idOrderApproval] || 0) + 1;
            }
            const keepIdOrderApproval = Object.keys(count).includes("undefined") ? undefined : Object.keys(count).reduce((a, b) => count[a] > count[b] ? a : b);
            const moveApprovalPending = await promptApprovalConflict({keepIdOrderApproval});
            if (moveApprovalPending) {
                const itemIds = resultCart.items.filter((item) => item.approvalInfo?.idOrderApproval != keepIdOrderApproval).map((item) => item.itemId);
                await apis.next.cart.deleteItemsFromCart({cartId: resultCart.cartId, itemIds: itemIds});
                alert("Success! All approval orders have been moved to pending.");
                await store.dispatch(reloadCart({apis}));
            }
        }
    })],
    ["apiErrors", (_, next) => UseState({next})],
    ["catchApiErrors", ({apiErrors}, next) => next(async ({}, next) => {
        apiErrors.onChange(null);
        try {
            await next();
            return true;
        } catch (e:any) {
            if (e.paymentErrors?.billing) {
                const errors = e.paymentErrors.billing.filter((e1) => e1.type);
                apiErrors.onChange(errors);

                if (errors?.length) {
                    alert("Error Applying: " + errors[0].error)
                }

                chain(
                    e.paymentErrors.billing.find((e1) => !e1.type),
                    (generalError) => generalError && alert(generalError.error),
                );
            } else {
                alert(typeof e.errors === "string" ? e.errors : e.errors.msg);
            }
            return false;
        }
    })],

    ["base", ({billingAccounts, addBilling, catchApiErrors, actions}, next) => CorporateBillingAccountsBase({
        billingAccounts,
        appliedBillings: store.cart?.appliedPayments?.billing,
        addBilling: ({billingId, fields}) => cs(
            catchApiErrors,
            async () => addBilling({billingId, fields}),
        ),
        updateBilling: ({billingId, fields}) => cs(
            catchApiErrors,
            async () => await store.dispatch(
                actions.updateBillingAccountFields({billingId, fields})
            ),
        ),
        removeBilling: () => cs(
            catchApiErrors,
            async () => await store.dispatch(actions.removeBillingAccounts()),
        ),
        next,
    })],
    ({base, apiErrors}) => rootNext({
        invalid: base.saving || base.invalid || apiErrors.value?.length,
        highlightErrors: base.highlightErrors,
        render: ({showErrors, className}) => PaymentPanel({
            title: "Corporate Billing Accounts",
            className: cx("corporate-billing-accounts-5tg", className),
            hasError: showErrors && (base.hasError || apiErrors.value?.length),
            controls: {
                status: base.saving && "Saving...",
                checkbox: {
                    ...base.masterToggle,

                    xdQaName: "Corporate Billing Accounts"
                },
            },
            content: () => (
                base.renderList({showErrors, apiErrors: apiErrors.value})
            ),
        }),
    }),
);


const toAmount = (amount, total, unitPrice) => {
    const amountAvailable = (
        amount.endsWith("%") ? (
            +amount.substring(0, amount.length - 1) / 100 * total
        ) : amount.startsWith("$") ? (
            +amount.substring(1)
        ) : (
            +amount * unitPrice
        )
    );
    return amountAvailable < total ? amountAvailable : total;
};
